import { _IdGenerator } from '@angular/cdk/a11y';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ContentChild,
  ContentChildren,
  Directive,
  inject,
  input,
  OnDestroy,
  output,
  QueryList,
  ViewEncapsulation,
} from '@angular/core';
import { AbstractControl, FormsModule, NgControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { merge, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HxIconButton } from '../icon-button/icon-button';
import { HxLabel } from '../label/label';
import { DefaultErrorStateMatcher, ErrorStateMatcher } from './error-state-matcher';
import { HX_FORM_FIELD_CONTROL, HxFormFieldControl } from './form-field-control';
/**
 * HxLabel - Directive for form group label Manages label content and required indicator display.
 *
 * @example
 *   <label hx-label [required]="true" for="email">
 *   Email Address
 *
 * @if (required()) {
 *     <span class="required" aria-hidden="true">*</span>
 *   }
 * </label>
 *
 * @feature 011-harmony-form-field
 */

/**
 * HxHint - Directive for form group helper text Auto-generates unique ID for aria-describedby association. Hidden when
 * errors are shown (errors take priority).
 *
 * @example
 *   <div hx-hint>Enter your work email address</div>
 *   <!-- Rendered: <div hx-hint id="hx-hint-abc123" class="hx-form-hint">... -->
 *
 * @feature 011-harmony-form-field
 */
@Directive({
  selector: 'hx-hint, [hx-hint]',
  standalone: true,
  host: {
    class: 'hx-form-hint',
    '[id]': 'hintId',
  },
})
export class HxHint {
  private readonly _idGenerator = inject(_IdGenerator);

  /** Auto-generated unique ID for aria-describedby */
  readonly hintId = this._idGenerator.getId('hx-hint');
}

/**
 * HxError - Directive for validation error messages Shown only when parent HxFormGroup is in error state. Wrapped in
 * aria-live="polite" container for screen reader announcements. Replaces HxHint content (shares same space).
 *
 * @if (emailControl.errors?.['required']) {
 *   <div hx-error>Email is required</div>
 * } @else if (emailControl.errors?.['email']) {
 *   <div hx-error>Please enter a valid email address</div>
 * }
 *
 * @feature 011-harmony-form-field
 */
@Directive({
  selector: 'hx-error, [hx-error]',
  standalone: true,
  host: {
    class: 'hx-form-error',
    '[id]': 'errorId',
  },
})
export class HxError {
  private readonly _idGenerator = inject(_IdGenerator);

  /** Auto-generated unique ID for aria-describedby */
  readonly errorId = this._idGenerator.getId('hx-error');
}

/**
 * HxPrefix - Directive for text prefix content before input Similar to Angular Material's matTextPrefix. Used for text
 * content before the input element.
 *
 * @example
 *   <hx-form-group>
 *   <span hxPrefix>$</span>
 *   <input hxInput type="number" />
 *   </hx-form-group>
 *
 * @feature 012-harmony-input
 */
@Directive({
  selector: '[hxPrefix], [hxTextPrefix]',
  standalone: true,
  host: {
    class: 'hx-form-prefix',
  },
})
export class HxPrefix {
  _isText = true;
}

/**
 * HxIconPrefix - Directive for icon prefix content before input Similar to Angular Material's matIconPrefix. Used for
 * icons/buttons before the input element.
 *
 * @example
 *   <hx-form-group>
 *   <hx-icon hxIconPrefix svgIcon="email" />
 *   <input hxInput type="email" />
 *   </hx-form-group>
 *
 * @feature 012-harmony-input
 */
@Directive({
  selector: '[hxIconPrefix]',
  standalone: true,
  host: {
    class: 'hx-form-prefix',
  },
})
export class HxIconPrefix {
  _isText = false;
}

/**
 * HxSuffix - Directive for text suffix content after input Similar to Angular Material's matTextSuffix. Used for text
 * content after the input element.
 *
 * @example
 *   <hx-form-group>
 *   <input hxInput type="number" />
 *   <span hxSuffix>.00</span>
 *   </hx-form-group>
 *
 * @feature 012-harmony-input
 */
@Directive({
  selector: '[hxSuffix], [hxTextSuffix]',
  standalone: true,
  host: {
    class: 'hx-form-suffix',
  },
})
export class HxSuffix {
  _isText = true;
}

/**
 * HxIconSuffix - Directive for icon suffix content after input Similar to Angular Material's matIconSuffix. Used for
 * icons/buttons after the input element.
 *
 * @example
 *   <hx-form-group>
 *   <input hxInput type="password" />
 *   <button hxIconSuffix type="button">
 *   <hx-icon svgIcon="eye" />
 *   </button>
 *   </hx-form-group>
 *
 * @feature 012-harmony-input
 */
@Directive({
  selector: '[hxIconSuffix]',
  standalone: true,
  host: {
    class: 'hx-form-suffix',
  },
})
export class HxIconSuffix {
  _isText = false;
}

/**
 * HxFormGroup - Wrapper component for form controls following Moon Design System Provides consistent label, control,
 * and hint layout with automatic error detection.
 *
 * @example
 *   // Basic usage with automatic error detection
 *   <hx-form-group>
 *   <label hx-label [required]="true" for="email">Email</label>
 *   <input type="email" id="email" [formControl]="emailControl" />
 *   <div hx-hint>Enter your work email</div>
 *
 * @example
 *   // With custom error state matcher
 *   <hx-form-group [errorStateMatcher]="immediateErrorMatcher">
 *   <label hx-label for="password">Password</label>
 *   <input type="password" id="password" [formControl]="passwordControl" />
 *   <div hx-hint>Minimum 8 characters</div>
 *   </hx-form-group>
 *
 * @example
 *   // Manual error override
 *   <hx-form-group [error]="customErrorLogic()">
 *   <label hx-label for="field">Field</label>
 *   <input id="field" />
 *   <div hx-hint>Helper text</div>
 *   <div hx-error>Custom error message</div>
 *   </hx-form-group>
 *
 * @if (emailControl.errors?.['required']) {
 *     <div hx-error>Email is required</div>
 *   }
 * </hx-form-group>
 *
 * @feature 011-harmony-form-field
 */
@Component({
  selector: 'hx-form-group',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, HxIconButton],
  templateUrl: './form-group.html',
  styleUrls: ['./form-group.css', '../../styles/form-control-tokens.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'hx-form-group',
    '[class.hx-form-group-error]': '_shouldShowError()',
    '[class.hx-justify-left]': 'justify() === "left"',
    '[class.hx-justify-center]': 'justify() === "center"',
    '[class.hx-justify-right]': 'justify() === "right"',
  },
})
export class HxFormGroup implements AfterContentInit, OnDestroy {
  private readonly _changeDetectorRef = inject(ChangeDetectorRef);
  private readonly _destroyed = new Subject<void>();
  private readonly _defaultErrorStateMatcher = new DefaultErrorStateMatcher();

  // === SIGNAL INPUTS ===

  /**
   * Manually override error state (optional, auto-detected if FormControl present). When undefined, error state is
   * automatically detected from projected NgControl.
   */
  readonly error = input<boolean | undefined>(undefined);

  /**
   * Custom error state matcher for advanced error display logic. When undefined, uses DefaultErrorStateMatcher (show
   * errors when invalid && (touched || dirty)).
   */
  readonly errorStateMatcher = input<ErrorStateMatcher | undefined>(undefined);

  /** Hide required marker even when control is required (default: false) */
  readonly hideRequiredMarker = input<boolean>(false);

  /** Horizontal justification for label, input text, and subscript content (default: 'left') */
  readonly justify = input<'left' | 'center' | 'right'>('left');

  /** Show trash icon to allow block removal (optional, default: false) */
  readonly removable = input<boolean>(false);

  /** Show toggle switch to control required state (optional, default: false) */
  readonly showRequiredToggle = input<boolean>(false);

  /** Emit when trash icon is clicked (for parent to handle block removal) */
  readonly onRemove = output<void>();

  /** Emit when required toggle changes (true = required, false = optional) */
  readonly onRequiredToggle = output<boolean>();

  // === CONTENT QUERIES ===

  /** Projected HxFormFieldControl (HxTextarea, HxInput, etc.) for enhanced integration */
  @ContentChild(HX_FORM_FIELD_CONTROL)
  private readonly _formFieldControl?: HxFormFieldControl<any>;

  /** Projected FormControl/NgModel for automatic error detection (fallback if no HxFormFieldControl) */
  @ContentChild(NgControl)
  private readonly _control?: NgControl;

  // NEW: Grab the label to update its required state automatically
  @ContentChild(HxLabel)
  private readonly _label?: HxLabel;

  /** Projected HxHint directive for aria-describedby wiring */
  @ContentChild(HxHint)
  private readonly _hint?: HxHint;

  /** Projected HxError directive(s) for error message display */
  @ContentChildren(HxError)
  private readonly _errors?: QueryList<HxError>;

  /** Projected prefix directives (text and icons) */
  @ContentChildren(HxPrefix, { descendants: true })
  private readonly _prefixChildren?: QueryList<HxPrefix>;

  /** Projected icon prefix directives */
  @ContentChildren(HxIconPrefix, { descendants: true })
  private readonly _iconPrefixChildren?: QueryList<HxIconPrefix>;

  /** Projected suffix directives (text and icons) */
  @ContentChildren(HxSuffix, { descendants: true })
  private readonly _suffixChildren?: QueryList<HxSuffix>;

  /** Projected icon suffix directives */
  @ContentChildren(HxIconSuffix, { descendants: true })
  private readonly _iconSuffixChildren?: QueryList<HxIconSuffix>;

  // === PREFIX/SUFFIX STATE FLAGS ===
  protected _hasIconPrefix = false;
  protected _hasTextPrefix = false;
  protected _hasIconSuffix = false;
  protected _hasTextSuffix = false;

  // === CONTROL STATE ===
  protected _isRequiredToggle = false;

  // === COMPUTED STATE ===

  /**
   * Checks if there's any subscript content (hint or errors) to display. Used to conditionally render the subscript
   * wrapper and avoid empty space.
   *
   * - Hints are always shown when projected.
   * - Projected `hx-error` elements are only counted when the control is in error state, preventing the empty wrapper
   *   from appearing for untouched required fields.
   */
  protected _hasSubscriptContent(): boolean {
    if (this._hint) {
      return true;
    }
    // Only count projected errors when they should actually be shown
    return (this._errors?.length ?? 0) > 0 && this._shouldShowError();
  }

  /**
   * Computed error state (manual override OR auto-detected from control). Manual error input takes precedence over
   * automatic detection. Used in host binding [class.hx-form-group-error].
   */
  protected readonly _shouldShowError = computed(() => {
    // Manual override takes precedence
    const manualError = this.error();
    if (manualError !== undefined) {
      return manualError;
    }

    // Auto-detect from FormControl
    const control = this._control?.control;
    if (!control) {
      return false;
    }

    // Use custom matcher or default
    const matcher = this.errorStateMatcher() || this._defaultErrorStateMatcher;
    return matcher.isErrorState(control, null);
  });

  // === LIFECYCLE HOOKS ===

  ngAfterContentInit(): void {
    // Initialize prefix/suffix detection
    this._updatePrefixAndSuffix();

    // Subscribe to prefix/suffix changes
    if (this._prefixChildren) {
      this._prefixChildren.changes.pipe(takeUntil(this._destroyed)).subscribe(() => {
        this._updatePrefixAndSuffix();
        this._changeDetectorRef.markForCheck();
      });
    }
    if (this._iconPrefixChildren) {
      this._iconPrefixChildren.changes.pipe(takeUntil(this._destroyed)).subscribe(() => {
        this._updatePrefixAndSuffix();
        this._changeDetectorRef.markForCheck();
      });
    }
    if (this._suffixChildren) {
      this._suffixChildren.changes.pipe(takeUntil(this._destroyed)).subscribe(() => {
        this._updatePrefixAndSuffix();
        this._changeDetectorRef.markForCheck();
      });
    }
    if (this._iconSuffixChildren) {
      this._iconSuffixChildren.changes.pipe(takeUntil(this._destroyed)).subscribe(() => {
        this._updatePrefixAndSuffix();
        this._changeDetectorRef.markForCheck();
      });
    }

    // Subscribe to control state changes for reactive error detection
    if (this._control?.control) {
      // NEW: Perform initial check for required validator
      this._updateRequiredState();

      merge(this._control.control.statusChanges || of(), this._control.control.valueChanges || of())
        .pipe(takeUntil(this._destroyed))
        .subscribe(() => {
          // Trigger change detection when control state changes
          this._changeDetectorRef.markForCheck();
          // NEW: Re-evaluate required state (in case validators are added/removed dynamically)
          this._updateRequiredState();
        });

      // Wire aria-describedby to control
      this._updateAriaDescribedBy();
    }
  }

  ngOnDestroy(): void {
    this._destroyed.next();
    this._destroyed.complete();
  }

  // === PRIVATE METHODS ===

  /** Updates the HxLabel with the required state of the underlying control. */
  private _updateRequiredState(): void {
    if (this._label && !this.hideRequiredMarker()) {
      const control = this._control?.control;
      if (control) {
        const isRequired = this._hasRequiredValidator(control);
        //this._label.required.set(isRequired);
      }
    }
  }

  /** Checks if the control has a 'required' validator. */
  private _hasRequiredValidator(control: AbstractControl): boolean {
    if (control.hasValidator) {
      return control.hasValidator(Validators.required);
    }
    // Fallback for older Angular versions or custom setups
    if (control.validator) {
      const validator = control.validator({} as AbstractControl);
      return !!(validator && validator['required']);
    }
    return false;
  }

  /**
   * Detects presence of icon vs text prefix/suffix (Angular Material pattern). Updates flags used in template
   * conditionals.
   */
  private _updatePrefixAndSuffix(): void {
    this._hasIconPrefix = !!(this._iconPrefixChildren && this._iconPrefixChildren.length > 0);
    this._hasTextPrefix = !!(this._prefixChildren && this._prefixChildren.length > 0);
    this._hasIconSuffix = !!(this._iconSuffixChildren && this._iconSuffixChildren.length > 0);
    this._hasTextSuffix = !!(this._suffixChildren && this._suffixChildren.length > 0);
  }

  /**
   * Auto-wires aria-describedby to control element. Called in ngAfterContentInit() and whenever error state changes.
   *
   * Behavior:
   *
   * - When no errors: Adds hint ID to aria-describedby
   * - When errors shown: Replaces hint ID with error IDs
   * - Multiple errors: All error IDs added (space-separated)
   * - Updates dynamically as error state toggles
   */
  private _updateAriaDescribedBy(): void {
    // Try to find the native control element
    const controlElement = this._findControlElement();
    if (!controlElement) {
      return;
    }

    const describedByIds: string[] = [];

    // Add hint ID when no errors
    if (this._hint && !this._shouldShowError()) {
      describedByIds.push(this._hint.hintId);
    }

    // Add error IDs when errors shown
    if (this._errors && this._shouldShowError()) {
      this._errors.forEach((error) => describedByIds.push(error.errorId));
    }

    if (describedByIds.length > 0) {
      controlElement.setAttribute('aria-describedby', describedByIds.join(' '));
    } else {
      controlElement.removeAttribute('aria-describedby');
    }
  }

  /**
   * Finds the native control element from the projected NgControl. Handles different control value accessor
   * implementations.
   */
  private _findControlElement(): HTMLElement | null {
    if (!this._control?.valueAccessor) {
      return null;
    }

    const accessor = this._control.valueAccessor as any;

    // Try to get element from common patterns
    if (accessor._elementRef?.nativeElement) {
      return accessor._elementRef.nativeElement;
    }

    if (accessor.elementRef?.nativeElement) {
      return accessor.elementRef.nativeElement;
    }

    return null;
  }

  /** Check if there's a label projected */
  protected _hasLabel(): boolean {
    return !!this._label;
  }

  /** Toggles required state and emits onRequiredToggle event */
  _toggleRequired(): void {
    this._isRequiredToggle = !this._isRequiredToggle;
    this.onRequiredToggle.emit(this._isRequiredToggle);
    this._changeDetectorRef.markForCheck();
  }

  /** Emits onRemove event for parent component to handle block removal */
  _removeBlock(): void {
    this.onRemove.emit();
  }
}
